import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  getTargets,
  toggleTarget,
  updateInterval,
  getRecords,
  getRecipients,
  addRecipient as apiAddRecipient,
  removeRecipient as apiRemoveRecipient,
  type HealthTarget,
  type HealthRecord,
  type HealthStatus,
  type IntervalMode,
  type HealthRecipient,
} from "@/services/healthMonitor";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import AdminDataTable from "@/components/admin/AdminDataTable";
import { AdminSelectFilter, AdminTableToolbar } from "@/components/admin/AdminTableToolbar";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HISTORY_PAGE_SIZE = 20;
const TARGET_SKELETON_COUNT = 2;

function formatDuration(ms: number) {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

function timeAgoShort(
  dateStr: string,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  const date = new Date(dateStr.replace(" ", "T"));
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (diffMin < 1) return t("healthMonitor.timeAgo.justNow");
  if (diffMin < 60) {
    const key = diffMin === 1 ? "healthMonitor.timeAgo.minute" : "healthMonitor.timeAgo.minutes";
    return t(key, { count: diffMin });
  }
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    const key = diffHour === 1 ? "healthMonitor.timeAgo.hour" : "healthMonitor.timeAgo.hours";
    return t(key, { count: diffHour });
  }
  const diffDay = Math.floor(diffHour / 24);
  const key = diffDay === 1 ? "healthMonitor.timeAgo.day" : "healthMonitor.timeAgo.days";
  return t(key, { count: diffDay });
}

function IntervalEditor({
  mode,
  value,
  onModeChange,
  onValueChange,
  t,
}: {
  mode: IntervalMode;
  value: number;
  onModeChange: (m: IntervalMode) => void;
  onValueChange: (v: number) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const minutes = [10, 20, 30, 45, 60];
  const hours = Array.from({ length: 24 }, (_, i) => i + 1);
  const days = Array.from({ length: 30 }, (_, i) => i + 1);
  const options = mode === "minutes" ? minutes : mode === "hours" ? hours : days;

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="flex items-center gap-2">
      {/* Mode selector */}
      <div className="flex items-center rounded-md border border-gray-200 bg-white overflow-hidden">
        <button
          onClick={() => {
            onModeChange("minutes");
            if (!minutes.includes(value)) onValueChange(10);
          }}
          className={`px-2.5 py-1 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap ${
            mode === "minutes" ? "text-white" : "text-gray-500 hover:bg-gray-50"
          }`}
          style={mode === "minutes" ? { backgroundColor: "#830051" } : undefined}
        >
          {t("healthMonitor.intervalMode.minutes")}
        </button>
        <button
          onClick={() => {
            onModeChange("hours");
            if (value > 24) onValueChange(1);
          }}
          className={`px-2.5 py-1 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap ${
            mode === "hours" ? "text-white" : "text-gray-500 hover:bg-gray-50"
          }`}
          style={mode === "hours" ? { backgroundColor: "#830051" } : undefined}
        >
          {t("healthMonitor.intervalMode.hours")}
        </button>
        <button
          onClick={() => {
            onModeChange("days");
            onValueChange(1);
          }}
          className={`px-2.5 py-1 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap ${
            mode === "days" ? "text-white" : "text-gray-500 hover:bg-gray-50"
          }`}
          style={mode === "days" ? { backgroundColor: "#830051" } : undefined}
        >
          {t("healthMonitor.intervalMode.days")}
        </button>
      </div>

      {/* Custom dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-1 text-[11px] border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700 cursor-pointer focus:outline-none focus:border-gray-300 min-w-[52px] justify-between"
        >
          <span>{value}</span>
          <span className="w-3 h-3 flex items-center justify-center text-gray-400">
            <i
              className={`ri-arrow-down-s-line transition-transform ${open ? "rotate-180" : ""}`}
            ></i>
          </span>
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-[160px] overflow-y-auto py-1">
            {options.map((n) => {
              const active = n === value;
              return (
                <button
                  key={n}
                  onClick={() => {
                    onValueChange(n);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-2.5 py-1 text-[11px] cursor-pointer transition-colors ${
                    active ? "text-white font-medium" : "text-gray-700 hover:bg-gray-50"
                  }`}
                  style={active ? { backgroundColor: "#830051" } : undefined}
                >
                  {n}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <span className="text-[11px] text-gray-400">
        {mode === "minutes"
          ? t("healthMonitor.intervalRangeMinutes")
          : mode === "hours"
            ? t("healthMonitor.intervalRangeHours")
            : t("healthMonitor.intervalRangeDays")}
      </span>
    </div>
  );
}

function ResultDetailModal({
  record,
  onClose,
  t,
}: {
  record: HealthRecord;
  onClose: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const statusBadgeClass =
    record.status === "healthy"
      ? "bg-green-50 text-green-700 border border-green-200"
      : "bg-red-50 text-red-700 border border-red-200";
  const statusLabel =
    record.status === "healthy"
      ? t("healthMonitor.item.healthy")
      : t("healthMonitor.item.critical");

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg mx-4 flex flex-col overflow-hidden"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
            style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
          >
            <i className="ri-file-text-line text-base" style={{ color: "#830051" }}></i>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("healthMonitor.resultDetail.title")}
            </h3>
            <p className="text-[11px] text-gray-400 mt-0.5 truncate">
              {record.name || record.endpoint}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <div className="text-[11px] text-gray-400 mb-0.5">
                {t("healthMonitor.col.checkTime")}
              </div>
              <div className="text-xs font-mono text-gray-700">{record.checkedAt}</div>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2.5">
              <div className="text-[11px] text-gray-400 mb-0.5">
                {t("healthMonitor.col.status")}
              </div>
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full ${statusBadgeClass}`}
              >
                <span
                  className={`w-2 h-2 rounded-full inline-block ${record.status === "healthy" ? "bg-green-500" : "bg-red-500"}`}
                />
                {statusLabel}
              </span>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-3">
            <div className="text-[11px] text-gray-400 mb-1.5">{t("healthMonitor.col.result")}</div>
            <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all font-sans leading-relaxed max-h-[320px] overflow-y-auto">
              {record.error}
            </pre>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end">
          <button
            onClick={onClose}
            className="text-xs px-4 py-1.5 rounded-lg border border-gray-200 text-gray-500 whitespace-nowrap cursor-pointer hover:bg-gray-50 transition-colors"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

function HealthTargetCardSkeleton() {
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-5 py-4 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-4 bg-gray-100 rounded w-36" />
            <div className="h-4 bg-gray-100 rounded w-10" />
          </div>
          <div className="h-5 bg-gray-100 rounded w-44" />
        </div>
        <div className="flex items-center gap-3">
          <div className="h-5 bg-gray-100 rounded-full w-20" />
          <div className="h-5 bg-gray-100 rounded-full w-9" />
        </div>
      </div>
      <div className="border-t border-gray-100 my-3" />
      <div className="flex items-center justify-between mb-3">
        <div className="h-3 bg-gray-100 rounded w-40" />
        <div className="h-3 bg-gray-100 rounded w-24" />
      </div>
      <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
        <div className="h-3 bg-gray-100 rounded w-48" />
        <div className="h-5 bg-gray-100 rounded w-16" />
      </div>
    </div>
  );
}

export default function HealthMonitorPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<HealthTarget[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(true);
  const [apiFilter, setApiFilter] = useState<string>("all");
  const [editingIntervalCode, setEditingIntervalCode] = useState<string | null>(null);
  const [intervalSaving, setIntervalSaving] = useState(false);

  // Draft state for the modal
  const [draftMode, setDraftMode] = useState<IntervalMode>("hours");
  const [draftValue, setDraftValue] = useState<number>(1);

  // History records
  const [records, setRecords] = useState<HealthRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPageSize, setRecordsPageSize] = useState(HISTORY_PAGE_SIZE);
  const [healthyCount, setHealthyCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [viewingResult, setViewingResult] = useState<HealthRecord | null>(null);

  // Alert recipients state
  const [recipients, setRecipients] = useState<HealthRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [recipientAdding, setRecipientAdding] = useState(false);

  // Modal draft state
  const [modalNewEmail, setModalNewEmail] = useState("");
  const [modalEmailError, setModalEmailError] = useState("");

  // ─── Data loading ──────────────────────────────────────────────────────────

  const loadTargets = useCallback(async () => {
    try {
      setTargetsLoading(true);
      const data = await getTargets();
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setTargetsLoading(false);
    }
  }, []);

  const loadRecords = useCallback(async (filter: string, page: number, pageSize: number) => {
    try {
      setRecordsLoading(true);
      const res = await getRecords(filter, page, pageSize);
      setRecords(res.data);
      setRecordsTotal(res.total);
      setHealthyCount(res.healthyCount);
      setCriticalCount(res.criticalCount);
    } catch {
      setRecords([]);
      setRecordsTotal(0);
      setHealthyCount(0);
      setCriticalCount(0);
    } finally {
      setRecordsLoading(false);
    }
  }, []);

  const loadRecipients = useCallback(async () => {
    try {
      setRecipientsLoading(true);
      const data = await getRecipients();
      setRecipients(data);
    } catch {
      setRecipients([]);
    } finally {
      setRecipientsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTargets();
    loadRecipients();
  }, [loadTargets, loadRecipients]);

  useEffect(() => {
    loadRecords(apiFilter, recordsPage, recordsPageSize);
  }, [apiFilter, recordsPage, recordsPageSize, loadRecords]);

  // ─── Recipient actions ───────────────────────────────────────────────────────

  const addRecipient = async () => {
    if (recipientAdding) return;
    const trimmed = modalNewEmail.trim().toLowerCase();
    setModalEmailError("");

    if (!trimmed) return;
    if (!EMAIL_REGEX.test(trimmed)) {
      setModalEmailError(t("healthMonitor.recipients.invalidEmail"));
      return;
    }
    if (recipients.some((r) => r.email === trimmed)) {
      setModalEmailError(t("healthMonitor.recipients.duplicate"));
      return;
    }

    setRecipientAdding(true);
    try {
      const created = await apiAddRecipient(trimmed);
      setRecipients((prev) => [...prev, created]);
      setModalNewEmail("");
    } catch {
      setModalEmailError(t("common.error"));
    } finally {
      setRecipientAdding(false);
    }
  };

  const removeRecipient = async (id: string) => {
    try {
      await apiRemoveRecipient(id);
      setRecipients((prev) => prev.filter((r) => r.id !== id));
    } catch {
      // silently fail
    }
  };

  // ─── Target actions ────────────────────────────────────────────────────────

  const statusDot = (status: HealthStatus) => {
    if (status === "healthy")
      return <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />;
    return <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />;
  };

  const statusBadgeClass = (status: HealthStatus) => {
    if (status === "healthy") return "bg-green-50 text-green-700 border border-green-200";
    return "bg-red-50 text-red-700 border border-red-200";
  };

  const statusLabel = (status: HealthStatus) => {
    if (status === "healthy") return t("healthMonitor.item.healthy");
    return t("healthMonitor.item.critical");
  };

  const toggleEnabled = async (code: string) => {
    const target = items.find((i) => i.code === code);
    if (!target) return;
    const next = !target.enabled;
    // optimistic update
    setItems((prev) => prev.map((i) => (i.code === code ? { ...i, enabled: next } : i)));
    try {
      const updated = await toggleTarget(code, next);
      setItems((prev) => prev.map((i) => (i.code === code ? updated : i)));
    } catch {
      // revert on failure
      setItems((prev) => prev.map((i) => (i.code === code ? { ...i, enabled: !next } : i)));
    }
  };

  const openIntervalEditor = (item: HealthTarget) => {
    setIntervalSaving(false);
    setDraftMode(item.intervalMode);
    setDraftValue(item.intervalValue);
    setEditingIntervalCode(item.code);
  };

  const confirmInterval = async () => {
    if (intervalSaving) return;
    const code = editingIntervalCode;
    if (!code) return;
    setIntervalSaving(true);
    try {
      const updated = await updateInterval(code, draftMode, draftValue);
      setItems((prev) => prev.map((i) => (i.code === code ? updated : i)));
      setEditingIntervalCode(null);
    } catch {
      // keep previous on failure
    } finally {
      setIntervalSaving(false);
    }
  };

  const apiFilterOptions = useMemo(
    () => [
      { value: "all", label: t("healthMonitor.filter.allApis") },
      ...items.map((it) => ({ value: it.code, label: it.name || it.endpoint })),
    ],
    [items, t],
  );

  const intervalLabel = (item: HealthTarget) => {
    const unit =
      item.intervalMode === "minutes"
        ? t("healthMonitor.intervalMode.minutes")
        : item.intervalMode === "hours"
          ? t("healthMonitor.intervalMode.hours")
          : t("healthMonitor.intervalMode.days");
    return t("healthMonitor.intervalPreview", { value: item.intervalValue, unit });
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <AdminPageTitle title={t("healthMonitor.title")} tooltip={t("healthMonitor.subtitle")} />
        </div>
      </div>

      {/* API info cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        {targetsLoading
          ? Array.from({ length: TARGET_SKELETON_COUNT }).map((_, index) => (
              <HealthTargetCardSkeleton key={index} />
            ))
          : items.map((item) => {
              const isEnabled = item.enabled;

              return (
                <div
                  key={item.code}
                  className={`rounded-xl bg-white border overflow-hidden transition-all duration-200 ${
                    isEnabled ? "border-gray-100" : "border-gray-100 bg-gray-50/50"
                  }`}
                >
                  <div className="px-5 pt-4 pb-4">
                    {/* Top row: endpoint + toggle */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3
                            className={`text-sm font-semibold ${isEnabled ? "text-gray-800" : "text-gray-400"}`}
                          >
                            {item.name || item.endpoint}
                          </h3>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border ${
                              isEnabled
                                ? "bg-gray-50 text-gray-500 border-gray-100"
                                : "bg-gray-100 text-gray-400 border-gray-100"
                            }`}
                          >
                            {item.method}
                          </span>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
                            isEnabled
                              ? "bg-gray-50 text-gray-500 border-gray-100"
                              : "bg-gray-100 text-gray-400 border-gray-100"
                          }`}
                        >
                          {item.endpoint}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          {statusDot(item.status)}
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${isEnabled ? statusBadgeClass(item.status) : "bg-gray-100 text-gray-400 border border-gray-200"}`}
                          >
                            {statusLabel(item.status)}
                          </span>
                        </div>
                        {/* Toggle switch */}
                        <button
                          onClick={() => toggleEnabled(item.code)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${
                            isEnabled ? "bg-emerald-500" : "bg-gray-300"
                          }`}
                          role="switch"
                          aria-checked={isEnabled}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                              isEnabled ? "translate-x-[18px]" : "translate-x-[4px]"
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-100 my-2" />

                    {/* Bottom row: last checked + fail count */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span>
                          {t("healthMonitor.lastChecked")}:{" "}
                          {item.lastCheckedAt ? timeAgoShort(item.lastCheckedAt, t) : "-"}
                        </span>
                        {item.failCount > 0 && (
                          <span className="text-red-500 font-medium">
                            ({item.failCount} {t("healthMonitor.consecutiveFails")})
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {item.lastError && (
                          <span className="text-[10px] text-red-500 truncate max-w-[140px]">
                            {item.lastError}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Interval - read-only display + edit button */}
                    <div
                      className={`mt-3 pt-3 border-t border-gray-50 flex items-center justify-between ${!isEnabled ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-gray-500">
                          {t("healthMonitor.interval")}
                        </span>
                        <span className="text-xs text-gray-700 font-medium">
                          {intervalLabel(item)}
                        </span>
                        {!isEnabled && (
                          <span className="text-[10px] text-gray-400">
                            - {t("healthMonitor.disabled")}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => openIntervalEditor(item)}
                        className="flex items-center gap-1 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap px-2 py-1 rounded-md hover:bg-gray-50"
                        style={{ color: "#830051" }}
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-edit-line text-xs"></i>
                        </span>
                        {t("healthMonitor.editInterval")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
      </div>

      {/* Alert Notification Configuration */}
      <div className="border border-gray-100 rounded-xl bg-white overflow-hidden mb-8">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
              style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
            >
              <i className="ri-mail-settings-line text-sm" style={{ color: "#830051" }}></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-800">
              {t("healthMonitor.alertConfig.sectionTitle")}
            </h3>
          </div>
          <button
            onClick={() => {
              setModalNewEmail("");
              setModalEmailError("");
              setRecipientAdding(false);
              setShowRecipientsModal(true);
            }}
            className="flex items-center gap-1 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap px-2.5 py-1 rounded-md hover:bg-white"
            style={{ color: "#830051" }}
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-edit-line text-xs"></i>
            </span>
            {t("common.edit")}
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-user-received-line text-sm"></i>
            </span>
            <span className="text-xs font-medium text-gray-700">
              {t("healthMonitor.recipients.title")}
            </span>
            {!recipientsLoading && (
              <span className="text-[10px] text-gray-400">({recipients.length})</span>
            )}
          </div>

          {/* Read-only recipients list */}
          <div className="space-y-1.5 min-h-[36px]">
            {recipientsLoading && (
              <div className="space-y-1.5 animate-pulse py-1">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center px-2.5 py-1.5 rounded-md border border-gray-100 bg-gray-50/70"
                  >
                    <div className="h-3 bg-gray-100 rounded w-48" />
                  </div>
                ))}
              </div>
            )}
            {!recipientsLoading && recipients.length === 0 && (
              <div className="text-[11px] text-gray-400 py-2">
                {t("healthMonitor.recipients.empty")}
              </div>
            )}
            {!recipientsLoading &&
              recipients.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center px-2.5 py-1.5 rounded-md border border-gray-100 bg-gray-50/70"
                >
                  <span className="text-xs text-gray-700 font-mono">{r.email}</span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Interval Edit Modal */}
      {editingIntervalCode &&
        (() => {
          const editingItem = items.find((i) => i.code === editingIntervalCode);
          if (!editingItem) return null;

          return (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
              onClick={() => {
                if (!intervalSaving) setEditingIntervalCode(null);
              }}
            >
              <div
                className="bg-white rounded-2xl w-full max-w-sm mx-4 flex flex-col overflow-visible"
                style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal header */}
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
                    style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
                  >
                    <i className="ri-timer-line text-base" style={{ color: "#830051" }}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {t("healthMonitor.intervalSettings")}
                    </h3>
                    <p className="text-[11px] text-gray-400 mt-0.5 truncate max-w-[280px]">
                      {editingItem.endpoint}
                    </p>
                  </div>
                </div>

                {/* Modal body */}
                <div className="px-5 py-4">
                  <label className="text-[11px] font-medium text-gray-600 block mb-2">
                    {t("healthMonitor.interval")}
                  </label>
                  <IntervalEditor
                    mode={draftMode}
                    value={draftValue}
                    onModeChange={setDraftMode}
                    onValueChange={setDraftValue}
                    t={t}
                  />
                </div>

                {/* Modal footer */}
                <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setEditingIntervalCode(null)}
                    disabled={intervalSaving}
                    className={`text-xs px-4 py-1.5 rounded-lg border border-gray-200 text-gray-500 whitespace-nowrap transition-colors ${
                      intervalSaving
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={confirmInterval}
                    disabled={intervalSaving}
                    className={`text-xs px-4 py-1.5 rounded-lg text-white whitespace-nowrap transition-opacity min-w-[82px] ${
                      intervalSaving
                        ? "cursor-not-allowed opacity-70"
                        : "cursor-pointer hover:opacity-90"
                    }`}
                    style={{ backgroundColor: "#830051" }}
                  >
                    {intervalSaving ? t("common.saving") : t("common.confirm")}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Recipients Management Modal */}
      {showRecipientsModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
          onClick={() => {
            if (!recipientAdding) setShowRecipientsModal(false);
          }}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md mx-4 flex flex-col overflow-visible"
            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
              <div
                className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
                style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
              >
                <i className="ri-mail-settings-line text-base" style={{ color: "#830051" }}></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("healthMonitor.recipients.manageRecipients")}
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {t("healthMonitor.recipients.manageDesc")}
                </p>
              </div>
            </div>

            {/* Modal body */}
            <div className="px-5 py-4 max-h-[360px] overflow-y-auto">
              {/* Recipients list */}
              <div className="space-y-1.5 mb-4 min-h-[32px]">
                {recipients.length === 0 && (
                  <div className="text-[11px] text-gray-400 py-2">
                    {t("healthMonitor.recipients.noRecipientsInModal")}
                  </div>
                )}
                {recipients.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between px-3 py-2 rounded-md border border-gray-100 bg-gray-50/70"
                  >
                    <span className="text-xs text-gray-700 font-mono">{r.email}</span>
                    <button
                      onClick={() => removeRecipient(r.id)}
                      className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors shrink-0"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>

              {/* Add email input */}
              <div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center text-gray-300">
                      <i className="ri-mail-line text-xs"></i>
                    </span>
                    <input
                      type="email"
                      value={modalNewEmail}
                      disabled={recipientAdding}
                      onChange={(e) => {
                        setModalNewEmail(e.target.value);
                        setModalEmailError("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addRecipient();
                      }}
                      placeholder={t("healthMonitor.recipients.addPlaceholder")}
                      className="w-full text-xs pl-7 pr-3 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 placeholder-gray-300 focus:outline-none focus:border-gray-300 transition-colors disabled:cursor-not-allowed disabled:opacity-70"
                    />
                  </div>
                  <button
                    onClick={addRecipient}
                    disabled={recipientAdding}
                    className={`text-xs px-3 py-1.5 rounded-md text-white whitespace-nowrap transition-opacity flex-shrink-0 min-w-[76px] ${
                      recipientAdding
                        ? "cursor-not-allowed opacity-70"
                        : "cursor-pointer hover:opacity-90"
                    }`}
                    style={{ backgroundColor: "#830051" }}
                  >
                    {recipientAdding ? t("common.saving") : t("common.add")}
                  </button>
                </div>
                {modalEmailError && (
                  <p className="text-[10px] text-red-500 mt-1 pl-1">{modalEmailError}</p>
                )}
              </div>
            </div>

            {/* Modal footer */}
            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end">
              <button
                onClick={() => setShowRecipientsModal(false)}
                disabled={recipientAdding}
                className={`text-xs px-4 py-1.5 rounded-lg border border-gray-200 text-gray-500 whitespace-nowrap transition-colors ${
                  recipientAdding
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer hover:bg-gray-50"
                }`}
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 历史记录 */}
      <div className="rounded-xl border border-gray-100 bg-white p-5 [&_th:nth-child(3)]:px-2 [&_th:nth-child(4)]:px-2">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-800">
              {t("healthMonitor.historyRecords")}
            </h3>
            <span className="text-xs text-gray-400">{t("healthMonitor.last24hRecords")}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
              {healthyCount} {t("healthMonitor.item.healthy")}
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
              {criticalCount} {t("healthMonitor.item.critical")}
            </span>
          </div>
        </div>

        <AdminTableToolbar>
          <AdminSelectFilter
            label={t("common.filter")}
            value={apiFilter}
            onChange={(value) => {
              setApiFilter(value);
              setRecordsPage(1);
            }}
            options={apiFilterOptions}
          />
        </AdminTableToolbar>

        <AdminDataTable
          headers={[
            t("healthMonitor.col.checkTime"),
            t("healthMonitor.col.name"),
            t("healthMonitor.col.status"),
            t("healthMonitor.col.responseTime"),
            t("healthMonitor.col.result"),
          ]}
          columnWidths={["15%", "40%", "20%", "15%", "10%"]}
          colSpan={5}
          loading={recordsLoading}
          empty={records.length === 0}
          emptyText={t("healthMonitor.table.noResults")}
          emptyHint={t("healthMonitor.table.noResultsHint")}
          total={recordsTotal}
          page={recordsPage}
          pageSize={recordsPageSize}
          onPageChange={(nextPage, nextPageSize) => {
            setRecordsPage(nextPage);
            setRecordsPageSize(nextPageSize);
          }}
        >
          {records.map((rec) => (
            <tr
              key={rec.id}
              className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors"
            >
              <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">{rec.checkedAt}</td>
              <td className="px-4 py-3 text-gray-600">
                <span className="inline-flex max-w-full items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-50 text-gray-600 border border-gray-100 truncate">
                  {rec.name || rec.endpoint}
                </span>
              </td>
              <td className="px-2 py-3 whitespace-nowrap">
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full ${statusBadgeClass(rec.status)}`}
                >
                  {statusDot(rec.status)}
                  {statusLabel(rec.status)}
                </span>
              </td>
              <td className="px-2 py-3 text-xs text-gray-700 whitespace-nowrap">
                {rec.status === "critical" ? (
                  <span className="text-gray-300">-</span>
                ) : (
                  <span>{formatDuration(rec.responseTimeMs)}</span>
                )}
              </td>
              <td className="px-3 py-3">
                {rec.error ? (
                  <button
                    type="button"
                    onClick={() => setViewingResult(rec)}
                    className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#830051] cursor-pointer transition-colors"
                    title={t("healthMonitor.viewError")}
                  >
                    <i className="ri-eye-line text-sm"></i>
                  </button>
                ) : rec.status === "healthy" ? (
                  <span className="inline-flex h-7 w-7 items-center justify-center text-green-600">
                    <i className="ri-check-line text-base"></i>
                  </span>
                ) : (
                  <span className="text-xs text-gray-300">-</span>
                )}
              </td>
            </tr>
          ))}
        </AdminDataTable>
      </div>

      {viewingResult && (
        <ResultDetailModal record={viewingResult} onClose={() => setViewingResult(null)} t={t} />
      )}
    </AdminLayout>
  );
}
